import { message } from 'antd'
import {
  applyMeasurementSystemToFormField,
  assetOverviewPermissionsCheck,
  createEditedObject,
  createUniqueFormFields,
} from 'components/form/formHelpers'
import {
  HandleSaveToRolodexType,
  SimpleFormField,
} from 'components/form/formTypes'
import { GeckoToggleForm } from 'components/form/GeckoToggleForm'
import { AssetDataObjType } from 'features/assetDatabase/types/assetOverviewTypes'
import { getJSONFromComponentFormFields } from 'features/assetDatabase/utils/assetDBComponents'
import React, { forwardRef, useMemo } from 'react'
import { SubmitHandler } from 'react-hook-form'
import {
  GeckoAssetModel,
  GeckoMeasurementSystem,
  regenerateAssetModel,
  RolodexEntryType,
} from 'types'

interface AssetModelComponentFormProps {
  assetDataObj: AssetDataObjType
  assetModel: GeckoAssetModel
  componentID: string
  measurementSystem: GeckoMeasurementSystem
  inEditMode: boolean
  inResolveMode: boolean
  userPermissions: ReadonlySet<string>
  forceRefresh: () => Promise<void>
  handleSaveToRolodex: HandleSaveToRolodexType
}

export const AssetModelComponentForm = forwardRef<
  HTMLFormElement,
  AssetModelComponentFormProps
>(
  (
    {
      assetDataObj,
      measurementSystem,
      assetModel,
      componentID,
      inEditMode,
      inResolveMode,
      userPermissions,
      forceRefresh,
      handleSaveToRolodex,
    },
    ref
  ) => {
    const [messageApi, contextHolder] = message.useMessage()

    const currentComponent = useMemo(
      () =>
        assetDataObj.components.filter(
          (component) => component.componentData.uniqueID === componentID
        )[0],
      [assetDataObj.components, componentID]
    )
    const currentComponentID = currentComponent.componentData.uniqueID
    const currentComponentFormFields = currentComponent.componentData.formFields
    const currentComponentJSON = currentComponent.componentData.componentJSON

    const formFieldsByUniqueID = useMemo(() => {
      return [
        {
          uniqueID: currentComponentID,
          formFields: currentComponentFormFields,
        },
      ]
    }, [currentComponentFormFields, currentComponentID])

    const componentFormOnSubmit: SubmitHandler<SimpleFormField> = (data) => {
      const convertedFormFields = createEditedObject(
        data,
        createUniqueFormFields(formFieldsByUniqueID)
      )
      const editedComponent = getJSONFromComponentFormFields(
        currentComponentJSON,
        convertedFormFields[0].formFields
      )
      const assetModelRaw = assetModel.raw
      const assetModelComponents = assetModelRaw.data.components as Record<
        string,
        unknown
      >
      assetModelComponents[convertedFormFields[0].uniqueID] = editedComponent
      const editedAssetModel = regenerateAssetModel(
        GeckoAssetModel.CreateFromJSON(assetModelRaw)
      )
      const rawComponent = {
        id: currentComponentID,
        type: RolodexEntryType.COMPONENT,
        data: { name: editedComponent.name },
        tags: { ...assetModel.tags },
        index: {},
        generation: 0,
        updated_at: '',
      }
      handleSaveToRolodex(
        [editedAssetModel, rawComponent],
        messageApi,
        forceRefresh,
        currentComponentID
      )
    }

    return (
      <>
        {contextHolder}
        <GeckoToggleForm
          formFieldsByUniqueID={formFieldsByUniqueID}
          inEditMode={inEditMode}
          inResolveMode={inResolveMode}
          permissionsCheck={{
            userPermissions: userPermissions,
            permissionsChecker: assetOverviewPermissionsCheck,
          }}
          measurementApplication={{
            measurementApplication: applyMeasurementSystemToFormField,
            measurementSystem: measurementSystem,
          }}
          onSubmit={componentFormOnSubmit}
          ref={ref}
        />
      </>
    )
  }
)

AssetModelComponentForm.displayName = 'AssetModelComponentForm'
